"use client"

import { useState, useEffect, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { ShoppingCart, Check, ChevronRight, Gift, Trophy } from "lucide-react"
import { FaCheck } from "react-icons/fa"
import axios from "axios"
import "../styles/compra.css"

const criarCamposVazios = () => ({
  nomeEsteira: "",
  cavaloPuxar: "",
  cavaloEsteira: "",
  representacao: "",
  cidade: "",
})

const CompraPasso3 = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const {
    cpf,
    nome,
    apelido,             // 🔹 NOVO: apelido para salvar no pedido
    categoriaSelecionada,
    evento,
    dadosPreenchidos,
    senhasSelecionadas = [0],
    senhasEscolhidas = {},
    senhasDefinidasPeloComprador = false,
  } = location.state || {}

  // ─── Dados da categoria ───────────────────────────────────────────────────
  const categoria = evento?.categorias?.find(c => c.nome === categoriaSelecionada)
  const taxasObrigatorias = evento?.taxasObrigatorias || []

  // ─── Helper: valor do Boi TV ciente do dia ────────────────────────────────
  // Se a categoria tiver boiTvPorDia configurado, usa o valor do dia escolhido.
  // Caso contrário, cai no valor antigo por senha (categoria.senhas[idx].boiTv).
  const getBoiTvValor = useCallback((slotIdx) => {
    const dia = senhasEscolhidas[slotIdx]?.dia
    const boiTvPorDia = categoria?.boiTvPorDia || []
    const regra = boiTvPorDia.find(r => r.dia === dia)
    if (regra !== undefined) return Number(regra.valor)
    return Number(categoria?.senhas?.[slotIdx]?.boiTv) || 0
  }, [senhasEscolhidas, categoria])

  // ─── Estado dos dados por senha ───────────────────────────────────────────
  const [usarMesmosDados, setUsarMesmosDados] = useState(true)
  const [dadosPorSenha, setDadosPorSenha] = useState(() => {
    const init = {}
    senhasSelecionadas.forEach(idx => {
      init[idx] = {
        nomeEsteira:   dadosPreenchidos?.nomeEsteira   || "",
        cavaloPuxar:   dadosPreenchidos?.cavaloPuxar   || "",
        cavaloEsteira: dadosPreenchidos?.cavaloEsteira || "",
        representacao: dadosPreenchidos?.representacao || "",
        cidade:        dadosPreenchidos?.cidade        || "",
      }
    })
    return init
  })

  const atualizarCampoSenha = (slotIdx, campo, valor) => {
    setDadosPorSenha(prev => {
      const novo = { ...prev }
      const indices = usarMesmosDados ? senhasSelecionadas : [slotIdx]
      indices.forEach(idx => {
        novo[idx] = { ...(novo[idx] || criarCamposVazios()), [campo]: valor }
      })
      return novo
    })
  }

  // ─── Boi TV ───────────────────────────────────────────────────────────────
  // Inicializa como true se o valor for 0 (grátis) — já vem selecionado
  const [boiTvSelecionado, setBoiTvSelecionado] = useState(() => {
    const init = {}
    senhasSelecionadas.forEach(idx => {
      init[idx] = getBoiTvValor(idx) === 0
    })
    return init
  })

  const toggleBoiTv = (slotIdx) => {
    setBoiTvSelecionado(prev => ({ ...prev, [slotIdx]: !prev[slotIdx] }))
  }

  // ─── Taxa opcional ────────────────────────────────────────────────────────
  const [taxaOpcionalSelecionada, setTaxaOpcionalSelecionada] = useState({})

  const toggleTaxaOpcional = (slotIdx) => {
    setTaxaOpcionalSelecionada(prev => ({ ...prev, [slotIdx]: !prev[slotIdx] }))
  }

  // ─── Raspadinha ───────────────────────────────────────────────────────────
  const [raspadinhaQtd, setRaspadinhaQtd] = useState(0)
  const [raspadinhaPreco, setRaspadinhaPreco] = useState(null)
  const [raspadinhaPremio, setRaspadinhaPremio] = useState("")
  const [raspadinhaImg, setRaspadinhaImg] = useState(null)

  useEffect(() => {
    async function fetchRaspadinha() {
      try {
        const res = await axios.get(
          "https://api.ticketdovaqueiro.com/api/raspadinha/config",
          { params: { eventoId: evento._id, categoria: categoriaSelecionada } }
        )
        if (res.data) {
          setRaspadinhaPreco(Number(res.data.preco))
          setRaspadinhaPremio(
            res.data.nomePremio || res.data.nome || res.data.premio?.nome || "Prêmio Surpresa"
          )
          setRaspadinhaImg(
            res.data.imagemPremioUrl || res.data.imagemPremio || res.data.premio?.imagemUrl || null
          )
        }
      } catch {
        // sem raspadinha ativa, ignora
      }
    }
    if (evento?._id) fetchRaspadinha()
  }, [evento, categoriaSelecionada])

  // ─── Total ────────────────────────────────────────────────────────────────
  const [totalPedido, setTotalPedido] = useState(0)

  const calcularTotal = useCallback(() => {
    let total = 0
    senhasSelecionadas.forEach((slotIdx) => {
      total += Number(categoria?.senhas?.[slotIdx]?.preco) || 0
      taxasObrigatorias.forEach(t => { total += Number(t.valor) || 0 })
      if (boiTvSelecionado[slotIdx]) total += getBoiTvValor(slotIdx)
      if (taxaOpcionalSelecionada[slotIdx]) {
        total += Number(categoria?.taxaOpcional?.valor) || 0
      }
    })
    if (raspadinhaPreco && raspadinhaQtd > 0) {
      total += raspadinhaPreco * raspadinhaQtd
    }
    setTotalPedido(total)
  }, [
    senhasSelecionadas,
    boiTvSelecionado,
    taxaOpcionalSelecionada,
    categoria,
    taxasObrigatorias,
    raspadinhaPreco,
    raspadinhaQtd,
    getBoiTvValor,
  ])

  useEffect(() => { calcularTotal() }, [calcularTotal])

  // ─── Áudio ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const audio = new Audio("/audios/passo2.mp3")
    audio.play().catch(e => console.error("Erro ao reproduzir áudio:", e))
    return () => { audio.pause(); audio.currentTime = 0 }
  }, [])

  // ─── Estados de UI ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState("")

  const formatarPreco = (v) =>
    Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })

  // ─── Validação e criação do pedido ───────────────────────────────────────
  const finalizarPedido = async () => {
    const primeiroIdx = senhasSelecionadas[0]
    const dadosPrimeira = dadosPorSenha[primeiroIdx] || criarCamposVazios()

    if (!dadosPrimeira.nomeEsteira.trim())   { alert("Preencha o campo Nome do Esteireiro!"); return }
    if (!dadosPrimeira.cavaloPuxar.trim())   { alert("Preencha o campo Cavalo de Puxar!"); return }
    if (!dadosPrimeira.cavaloEsteira.trim()) { alert("Preencha o campo Cavalo de Esteira!"); return }
    if (!dadosPrimeira.representacao.trim()) { alert("Preencha o campo Representação!"); return }
    if (!dadosPrimeira.cidade.trim())        { alert("Preencha o campo Cidade!"); return }

    if (!usarMesmosDados) {
      for (const idx of senhasSelecionadas) {
        const d = dadosPorSenha[idx] || criarCamposVazios()
        if (!d.nomeEsteira.trim() || !d.cavaloPuxar.trim() || !d.cavaloEsteira.trim() ||
            !d.representacao.trim() || !d.cidade.trim()) {
          alert(`Preencha todos os dados da senha ${senhasEscolhidas[idx]?.numero || idx + 1}!`)
          return
        }
      }
    }

    try {
      setLoading(true)
      let usuarioLogado
      try {
        usuarioLogado = JSON.parse(localStorage.getItem("user"))
      } catch {
        localStorage.removeItem("user")
      }
      if (!usuarioLogado?._id) {
        setModalMessage("Erro ao obter ID do usuário. Faça login novamente.")
        setModalVisible(true)
        return
      }

      // Monta as senhas para a API
      const senhasPayload = senhasSelecionadas.map((slotIdx) => {
        const escolhida = senhasEscolhidas[slotIdx]
        const valorBoiTv = getBoiTvValor(slotIdx)
        const usarTaxa = !!taxaOpcionalSelecionada?.[slotIdx]
        const dadosVaqueiro = dadosPorSenha[slotIdx] || dadosPrimeira

        return {
          numero:  escolhida.numero,
          dia:     escolhida.dia,       // ← dia registrado na senha
          status:  "reservada",
          boiTv:   !!boiTvSelecionado[slotIdx],
          valorBoiTv: boiTvSelecionado[slotIdx] ? valorBoiTv : 0,
          usarTaxaOpcional: usarTaxa,
          taxasOpcionaisSelecionadas: usarTaxa
            ? [{ nome: categoria?.taxaOpcional?.nome || "Taxa Opcional", valor: categoria?.taxaOpcional?.valor || 0 }]
            : [],
          dadosVaqueiro,
        }
      })

      const pedidoData = {
        usuarioId: usuarioLogado._id,
        eventoId:  evento._id,
        categoria: categoriaSelecionada,
        senhas:    senhasPayload,
        total:     totalPedido,
        nomeVaqueiro: nome,
        apelido:   apelido || null,  // 🔹 NOVO: apelido para os relatórios de juiz/locutor
        cpf,
        nomeEsteira:   dadosPrimeira.nomeEsteira,
        cavaloPuxar:   dadosPrimeira.cavaloPuxar,
        cavaloEsteira: dadosPrimeira.cavaloEsteira,
        representacao: dadosPrimeira.representacao,
        cidade:        dadosPrimeira.cidade,
        afiliadoCodigo: localStorage.getItem("evento_afiliado_ref"),
        raspadinha: {
          qtdComprada: raspadinhaQtd,
          valorTotal:  raspadinhaQtd * (raspadinhaPreco || 0),
          liberada:    false,
        },
      }

      const response = await axios.post(
        "https://api.ticketdovaqueiro.com/api/pedidos/criar",
        pedidoData
      )

      if (response.status === 201 && response.data) {
        localStorage.removeItem("evento_afiliado_ref")
        const pedidoId = response.data._id

        // Monta boiTvSelecionado e boiTvValores no formato esperado pelo ResumoPedido
        const boiTvValores = {}
        senhasSelecionadas.forEach(idx => { boiTvValores[idx] = getBoiTvValor(idx) })

        navigate("/resumo-pedido", {
          state: {
            nome,
            categoriaSelecionada,
            senhasSelecionadas,
            senhasEscolhidas,
            totalPedido,
            evento,
            pedidoId,
            boiTvSelecionado,
            boiTvValores,
            taxasOpcionaisSelecionadas: senhasSelecionadas.map(idx =>
              taxaOpcionalSelecionada?.[idx]
                ? { nome: categoria?.taxaOpcional?.nome || "Taxa Opcional", valor: categoria?.taxaOpcional?.valor || 0 }
                : null
            ),
          },
        })
      }
    } catch (error) {
      console.error("Erro ao criar pedido:", error)
      const mensagem = error.response?.data?.mensagem || "Erro ao criar pedido. Tente novamente."
      setModalMessage(mensagem)
      setModalVisible(true)
    } finally {
      setLoading(false)
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="compra-container2 fluxo-compra-simples passo3-simples">
      <header className="flow-simple-header">
        <span className="flow-step-badge">3 de 3</span>
        <div>
          <h1>Complete os dados</h1>
          <p>Preencha e confira o valor.</p>
        </div>
      </header>

      {senhasDefinidasPeloComprador && (
        <div className="compra-senhas-comprador-notice" role="status">
          <span aria-hidden="true">✓</span>
          <div>
            <strong>Senhas definidas pelo comprador</strong>
            <small>O link do mapa foi encerrado e não aceita mais alterações.</small>
          </div>
        </div>
      )}

      <div className="compra-grid2">
        {/* ── Formulário de dados ──────────────────────────────────────────── */}
        <div className="compra-form2">
          <label>Vaqueiro</label>
          <input type="text" value={nome} readOnly />

          {senhasSelecionadas.length > 1 && (
            <div className="usar-mesmos-dados">
              <input
                id="usarMesmosDados"
                type="checkbox"
                checked={usarMesmosDados}
                onChange={(e) => setUsarMesmosDados(e.target.checked)}
              />
              <label htmlFor="usarMesmosDados">
                Repetir os dados em todas
              </label>
            </div>
          )}

          {senhasSelecionadas.map((slotIdx, position) => {
            const escolhida = senhasEscolhidas[slotIdx]
            const dados = dadosPorSenha[slotIdx] || criarCamposVazios()
            const isPrimeira = position === 0

            return (
              <div key={slotIdx} className="dados-senha-card">
                {/* Cabeçalho da senha com dia e número */}
                <div className="dados-senha-header">
                  <strong>Senha {slotIdx + 1}</strong>
                  {escolhida && (
                    <span className="senha-dia-badge-form">
                      {escolhida.dia} • Nº {escolhida.numero}
                    </span>
                  )}
                  {!isPrimeira && usarMesmosDados && (
                    <span className="dados-igualmente">Dados repetidos</span>
                  )}
                </div>

                <label>Nome do esteireiro</label>
                <input
                  type="text"
                  value={dados.nomeEsteira}
                  onChange={(e) => atualizarCampoSenha(slotIdx, "nomeEsteira", e.target.value)}
                  readOnly={!isPrimeira && usarMesmosDados}
                />

                <label>Cavalo de puxar</label>
                <input
                  type="text"
                  value={dados.cavaloPuxar}
                  onChange={(e) => atualizarCampoSenha(slotIdx, "cavaloPuxar", e.target.value)}
                  readOnly={!isPrimeira && usarMesmosDados}
                />

                <label>Cavalo de esteira</label>
                <input
                  type="text"
                  value={dados.cavaloEsteira}
                  onChange={(e) => atualizarCampoSenha(slotIdx, "cavaloEsteira", e.target.value)}
                  readOnly={!isPrimeira && usarMesmosDados}
                />

                <label>Equipe / Representação</label>
                <input
                  type="text"
                  value={dados.representacao}
                  onChange={(e) => atualizarCampoSenha(slotIdx, "representacao", e.target.value)}
                  readOnly={!isPrimeira && usarMesmosDados}
                />

                <label>Cidade / UF</label>
                <input
                  type="text"
                  value={dados.cidade}
                  onChange={(e) => atualizarCampoSenha(slotIdx, "cidade", e.target.value)}
                  readOnly={!isPrimeira && usarMesmosDados}
                />
              </div>
            )
          })}

          {/* ── Raspadinha ──────────────────────────────────────────────────── */}
          {raspadinhaPreco && (
            <div className="raspadinha-oferta">
              <div className="rasp-headline pulse">
                <Gift className="gift-icon" />
                <div className="rasp-title">
                  Raspadinha
                </div>
              </div>
              <p className="rasp-sub">Concorra a:</p>
              <div className="rasp-premio">
                {raspadinhaImg
                  ? <img src={raspadinhaImg} alt={raspadinhaPremio} className="premio-img" />
                  : <Trophy className="premio-icon" />
                }
                <span className="premio-nome">{raspadinhaPremio}</span>
              </div>
              <p className="rasp-preco">Por apenas: <strong>{formatarPreco(raspadinhaPreco)}</strong></p>
              <div className="qty-control" role="group" aria-label="Quantidade de raspadinhas">
                <button type="button" className="qty-btn" onClick={() => setRaspadinhaQtd(q => Math.max(0, q - 1))}>−</button>
                <div className="qty-count" aria-live="polite">{raspadinhaQtd}</div>
                <button type="button" className="qty-btn" onClick={() => setRaspadinhaQtd(q => q + 1)}>+</button>
              </div>
            </div>
          )}
        </div>

        {/* ── Sidebar / Carrinho ───────────────────────────────────────────── */}
        <div className="cart-container">
          <div className="cart-header">
            <div className="cart-title">
              <ShoppingCart className="cart-icon" />
              <h3>{categoriaSelecionada}</h3>
            </div>
            <div className="cart-badge">
              {senhasSelecionadas.length} {senhasSelecionadas.length === 1 ? "senha" : "senhas"}
            </div>
          </div>

          <div className="cart-description">Toque nos opcionais para marcar.</div>

          <div className="cart-items">
            <AnimatePresence>
              {senhasSelecionadas.map((slotIdx) => {
                const escolhida = senhasEscolhidas[slotIdx]
                const precoSenha = Number(categoria?.senhas?.[slotIdx]?.preco) || 0
                const valorBoiTv = getBoiTvValor(slotIdx)

                return (
                  <motion.div
                    key={slotIdx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="cart-item"
                  >
                    {/* Cabeçalho da senha no carrinho */}
                    <div className="cart-item-header selected">
                      <div className="cart-item-title">
                        <div className="checkbox checked">
                          <Check className="check-icon" />
                        </div>
                        <span>
                          Senha {slotIdx + 1}
                          {escolhida && (
                            <small style={{ display: "block", color: "var(--primary)", fontSize: "0.75rem" }}>
                              {escolhida.dia} · Nº {escolhida.numero}
                            </small>
                          )}
                        </span>
                      </div>
                      <span className="cart-item-price">{formatarPreco(precoSenha)}</span>
                    </div>

                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="cart-item-details"
                    >
                      {/* Taxas obrigatórias */}
                      {taxasObrigatorias.map((taxa, idx) => (
                        <div className="fee-item" key={idx}>
                          <div className="fee-name">
                            <div className="checkbox checked"><Check className="check-icon" /></div>
                            <span>{taxa.nome} *</span>
                          </div>
                          <span className="fee-price">{formatarPreco(Number(taxa.valor))}</span>
                        </div>
                      ))}

                      {/* ── Boi TV — ciente do dia ─────────────────────────── */}
                      <div
                        className={`fee-item boi-tv ${boiTvSelecionado[slotIdx] ? "selected" : ""}`}
                        onClick={() => toggleBoiTv(slotIdx)}
                      >
                        <div className="fee-name">
                          <div className={`checkbox ${boiTvSelecionado[slotIdx] ? "checked" : ""}`}>
                            {boiTvSelecionado[slotIdx] && <Check className="check-icon" />}
                          </div>
                          <div>
                            <span>Boi TV</span>
                            {/* Badge de promoção quando o dia tem preço diferente do padrão */}
                            {categoria?.boiTvPorDia?.some(r => r.dia === escolhida?.dia) && (
                              <small
                                style={{
                                  display: "block",
                                  fontSize: "0.7rem",
                                  color: valorBoiTv === 0 ? "#4caf50" : "var(--primary)",
                                  fontWeight: 600,
                                }}
                              >
                                {valorBoiTv === 0 ? "🎉 Grátis neste dia!" : `Preço especial: ${formatarPreco(valorBoiTv)}`}
                              </small>
                            )}
                          </div>
                        </div>
                        <span className="fee-price">
                          {valorBoiTv === 0 ? "Grátis" : formatarPreco(valorBoiTv)}
                        </span>
                      </div>

                      {/* Taxa opcional */}
                      {categoria?.taxaOpcional?.nome && (
                        <div
                          className={`fee-item boi-tv ${taxaOpcionalSelecionada[slotIdx] ? "selected" : ""}`}
                          onClick={() => toggleTaxaOpcional(slotIdx)}
                        >
                          <div className="fee-name">
                            <div className={`checkbox ${taxaOpcionalSelecionada[slotIdx] ? "checked" : ""}`}>
                              {taxaOpcionalSelecionada[slotIdx] && <FaCheck className="check-icon" />}
                            </div>
                            <span>{categoria.taxaOpcional.nome}</span>
                          </div>
                          <div className="fee-price">
                            {formatarPreco(Number(categoria.taxaOpcional.valor))}
                          </div>
                        </div>
                      )}
                    </motion.div>

                    <div className="cart-item-divider"></div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>

          <div className="cart-note">
            <small>* Taxas obrigatórias</small>
          </div>

          <div className="cart-total">
            <span className="total-label">TOTAL</span>
            <motion.span
              key={totalPedido}
              initial={{ scale: 1.2, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="total-price"
            >
              {formatarPreco(totalPedido)}
            </motion.span>
          </div>

          <button className="cart-button" onClick={finalizarPedido} disabled={loading}>
            {loading ? "AGUARDE..." : <>REVISAR PEDIDO <ChevronRight className="button-icon" /></>}
          </button>
        </div>
      </div>

      {/* ── Modal ────────────────────────────────────────────────────────────── */}
      {modalVisible && (
        <div className="aviso-modal">
          <div className="aviso-conteudo">
            <div className="aviso-header"><h1>Aviso</h1></div>
            <div className="aviso-body"><p>{modalMessage}</p></div>
            <div className="aviso-footer">
              <button className="btn-entendi" onClick={() => setModalVisible(false)}>Entendi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompraPasso3
